import { NumberInput, Select, TextArea, TextInput } from '@la/ds-ui-components';
import { ProductProperty, PropertyValue, UserSelection } from '@la/types';
import { convertToKebabCase } from '@la/utilities';
import { CardDisplayType } from '../MemberRegistration.types';
import styles from './AddOnCard.module.scss';

function applySelectionChange(
  productId: number,
  propertyId: string,
  value: string,
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void,
  clearSelectionError: (propertyId: string) => void
): void {
  updateAddOnSelection(productId, propertyId, value);
  if (value.trim() !== '') {
    clearSelectionError(propertyId);
  }
}

export default function AddOnSelections({
  addOnQuantity,
  cardDisplayType,
  clearSelectionError,
  productId,
  properties,
  selectionsWithErrors,
  updateAddOnQuantity,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  addOnQuantity?: number;
  cardDisplayType: CardDisplayType;
  clearSelectionError: (propertyId: string) => void;
  productId: number;
  properties?: ProductProperty[];
  selectionsWithErrors: string[];
  updateAddOnQuantity?: (quantity: number) => void;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  return (
    <div className={styles.selections}>
      {properties ? (
        <SelectionFields
          clearSelectionError={clearSelectionError}
          properties={properties}
          productId={productId}
          selectionsWithErrors={selectionsWithErrors}
          updateAddOnSelection={updateAddOnSelection}
          userSelections={userSelections}
        />
      ) : null}
      {cardDisplayType === 'optional' &&
      addOnQuantity &&
      updateAddOnQuantity ? (
        <AddOnQuantityPicker
          addOnQuantity={addOnQuantity}
          productId={productId}
          updateAddOnQuantity={updateAddOnQuantity}
        />
      ) : null}
    </div>
  );
}

function SelectionFields({
  clearSelectionError,
  productId,
  properties,
  selectionsWithErrors,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  clearSelectionError: (propertyId: string) => void;
  productId: number;
  properties: ProductProperty[];
  selectionsWithErrors: string[];
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  function renderSelectionFields(properties: ProductProperty[]) {
    return properties.map((property) => {
      const hasError = selectionsWithErrors.includes(property.id.toString());
      switch (property.inputControlType) {
        case 'NUMERIC':
          return (
            <AddOnNumericInput
              clearSelectionError={clearSelectionError}
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              required={property.required}
              productId={productId}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        case 'PICK_LIST':
          return (
            <AddOnSelectMenu
              clearSelectionError={clearSelectionError}
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              productId={productId}
              required={property.required}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
              values={property.values ?? []}
            />
          );
        case 'TEXT_AREA':
          return (
            <AddOnTextArea
              clearSelectionError={clearSelectionError}
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              productId={productId}
              required={property.required}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        case 'TEXT_BOX':
          return (
            <AddOnTextInput
              clearSelectionError={clearSelectionError}
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              productId={productId}
              required={property.required}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        default:
          console.error(
            `Unsupported input control type: ${property.inputControlType}`
          );
          return null;
      }
    });
  }

  return <>{renderSelectionFields(properties)}</>;
}

function AddOnNumericInput({
  clearSelectionError,
  hasError,
  id,
  name,
  productId,
  required,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  clearSelectionError: (propertyId: string) => void;
  hasError: boolean;
  id: number;
  name: string;
  productId: number;
  required: boolean;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  const handleChange = (num: number) => {
    const value = Number.isFinite(num) ? num.toString() : '';
    updateAddOnSelection(productId, id.toString(), value);
    if (Number.isFinite(num)) {
      clearSelectionError(id.toString());
    }
  };

  return (
    <NumberInput
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      min={0}
      onChangeValue={handleChange}
      required={required}
      size="medium"
      value={currentValue}
    />
  );
}

function AddOnSelectMenu({
  clearSelectionError,
  hasError,
  id,
  name,
  productId,
  required,
  updateAddOnSelection,
  userSelections,
  values,
}: Readonly<{
  clearSelectionError: (propertyId: string) => void;
  hasError: boolean;
  id: number;
  name: string;
  productId: number;
  required: boolean;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
  values: PropertyValue[];
}>) {
  const options = values.map((option: PropertyValue) => ({
    label: option.name,
    value: option.id.toString(),
  }));

  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  function handleChange(value: string): void {
    applySelectionChange(
      productId,
      id.toString(),
      value,
      updateAddOnSelection,
      clearSelectionError
    );
  }

  return (
    <Select
      errorMessage="Please select an option"
      hasError={hasError}
      id={id.toString()}
      label={name}
      name={convertToKebabCase(name)}
      onChange={handleChange}
      options={options}
      placeholder="Select an option"
      required={required}
      size="medium"
      value={currentValue}
    ></Select>
  );
}

function AddOnTextArea({
  clearSelectionError,
  hasError,
  id,
  name,
  productId,
  required,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  clearSelectionError: (propertyId: string) => void;
  hasError: boolean;
  id: number;
  name: string;
  productId: number;
  required: boolean;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  function handleChange(value: string): void {
    applySelectionChange(
      productId,
      id.toString(),
      value,
      updateAddOnSelection,
      clearSelectionError
    );
  }

  return (
    <TextArea
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      maxLength={255}
      name={convertToKebabCase(name)}
      onChange={(evt) => handleChange(evt.target.value)}
      required={required}
      value={currentValue}
    />
  );
}

function AddOnTextInput({
  clearSelectionError,
  hasError,
  id,
  name,
  productId,
  required,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  clearSelectionError: (propertyId: string) => void;
  hasError: boolean;
  id: number;
  name: string;
  productId: number;
  required: boolean;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  function handleChange(value: string): void {
    applySelectionChange(
      productId,
      id.toString(),
      value,
      updateAddOnSelection,
      clearSelectionError
    );
  }

  return (
    <TextInput
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      maxLength={255}
      name={convertToKebabCase(name)}
      onChange={(evt) => handleChange(evt.target.value)}
      required={required}
      showCharacterCounter={false}
      size="medium"
      value={currentValue}
    />
  );
}

function AddOnQuantityPicker({
  addOnQuantity,
  productId,
  updateAddOnQuantity,
}: Readonly<{
  addOnQuantity: number;
  productId: number;
  updateAddOnQuantity: (quantity: number) => void;
}>) {
  const handleChange = (value: number) => {
    updateAddOnQuantity(value);
  };

  return (
    <NumberInput
      id={`${productId}-quantity`}
      label="Quantity"
      max={9}
      min={1}
      onChangeValue={handleChange}
      size="medium"
      value={addOnQuantity}
      width="50%"
    />
  );
}
